import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Chip,
    Collapse,
    IconButton,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Grid,
    Divider,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Fade,
    Tooltip
} from '@mui/material';
import {
    Download as DownloadIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { SegmentsMachineResponseDto, InventoryStatus } from '../../../types/sales';

interface RowProps {
    machine: SegmentsMachineResponseDto;
    isMobile: boolean;
    marginPercentage: number;
}

function MachineRow({ machine, isMobile, marginPercentage }: RowProps) {
    const [open, setOpen] = useState(false);

    // Helper function to apply margin
    const applyMargin = (price: number) => {
        return price * (1 + marginPercentage / 100);
    };

    const getInventoryStatusColor = (status: InventoryStatus) => {
        switch (status) {
            case InventoryStatus.IN_STOCK:
                return 'success';
            case InventoryStatus.OUT_OF_STOCK:
                return 'error';
            case InventoryStatus.PRE_ORDER:
                return 'warning';
            case InventoryStatus.DISCONTINUED:
                return 'default';
            default:
                return 'default';
        }
    };

    const formatInventoryStatus = (status: InventoryStatus) => {
        return status ? status.replace(/_/g, ' ') : 'N/A';
    };

    if (isMobile) {
        return (
            <Card sx={{
                mb: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                }
            }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight="600">
                        {machine.machineModelName}
                    </Typography>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                Batch Reference
                            </Typography>
                            <Typography variant="body1">{machine.batchReferenceId || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                Vendor
                            </Typography>
                            <Typography variant="body1">{machine.vendor}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                Algorithm
                            </Typography>
                            <Typography variant="body1">{machine.algorithm}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                Hashrate
                            </Typography>
                            <Typography variant="body1">
                                {machine.machineHashrate} {machine.hashrateUnit}
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                Power
                            </Typography>
                            <Typography variant="body1">{machine.machinePower} W</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                                Setup Type
                            </Typography>
                            <Typography variant="body1">{machine?.setupType}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Status
                            </Typography>
                            <Chip
                                label={formatInventoryStatus(machine.inventoryStatus)}
                                color={getInventoryStatusColor(machine.inventoryStatus)}
                                size="small"
                                sx={{ mt: 0.5 }}
                            />
                        </Grid>
                    </Grid>

                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setOpen(!open)}
                        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        sx={{ mt: 2 }}
                    >
                        {open ? 'Hide' : 'Show'} Pricing Details ({machine.prices.length})
                    </Button>

                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ mt: 2 }}>
                            {machine.prices
                                .map((price, index) => (
                                    <Box key={price.id} sx={{ mb: 2 }}>
                                        {index > 0 && <Divider sx={{ my: 2 }} />}
                                        <Typography variant="subtitle2" gutterBottom>
                                            Price Option {index + 1} (Hosting: ${price.hostingCost})
                                        </Typography>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Machine Price
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    ${applyMargin(price.machinePriceUsd).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Hosting Cost
                                                </Typography>
                                                <Typography variant="body1">
                                                    ${price.hostingCost}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Setup Fee
                                                </Typography>
                                                <Typography variant="body1">
                                                    ${price.setupFee.toFixed(2)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Deposit
                                                </Typography>
                                                <Typography variant="body1">
                                                    ${price.deposit ? price.deposit.toFixed(2) : '00'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Transport Fee
                                                </Typography>
                                                <Typography variant="body1">
                                                    ${price.transportFee.toFixed(2)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="primary" fontWeight="bold">
                                                    Total
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold" color="primary">
                                                    ${(applyMargin(price.machinePriceUsd) + price.setupFee + price.transportFee + (price.deposit || 0)).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ))}
                        </Box>
                    </Collapse>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <TableRow
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    transition: 'background-color 0.2s',
                    '&:hover': {
                        backgroundColor: 'action.hover'
                    }
                }}
            >
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                        sx={{
                            transition: 'transform 0.3s',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                    >
                        <KeyboardArrowDownIcon />
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    <Typography variant="body2" fontWeight="600">
                        {machine.machineModelName}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight="500">
                        {machine.batchReferenceId || 'N/A'}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{machine.vendor}</Typography>
                </TableCell>
                <TableCell>
                    <Chip label={machine.algorithm} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2" fontWeight="500">
                        {machine.machineHashrate} {machine.hashrateUnit}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2">{machine.machinePower} W</Typography>
                </TableCell>
                <TableCell>
                    <Chip
                        label={formatInventoryStatus(machine.inventoryStatus)}
                        color={getInventoryStatusColor(machine.inventoryStatus)}
                        size="small"
                    />
                </TableCell>
                <TableCell align="right">
                    <Typography variant="body2">{machine.setupType}</Typography>
                </TableCell>
                <TableCell align="center">
                    <Chip
                        label={machine.prices.length}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom component="div" fontWeight="600">
                                Pricing Details
                            </Typography>
                            <Table size="small" aria-label="pricing">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Machine Price (USD)</strong></TableCell>
                                        <TableCell align="right"><strong>Hosting Cost</strong></TableCell>
                                        <TableCell align="right"><strong>Setup Fee</strong></TableCell>
                                        <TableCell align="right"><strong>Transport Fee</strong></TableCell>
                                        <TableCell align="right"><strong>Deposit</strong></TableCell>
                                        <TableCell align="right"><strong>Total</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {machine.prices
                                        .map((price) => (
                                            <TableRow key={price.id}>
                                                <TableCell component="th" scope="row">
                                                    <Typography variant="body2" fontWeight="600" color="primary">
                                                        ${applyMargin(price.machinePriceUsd).toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    ${price.hostingCost}
                                                </TableCell>
                                                <TableCell align="right">
                                                    ${price.setupFee.toFixed(2)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    ${price.transportFee.toFixed(2)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    ${price.deposit ? price.deposit.toFixed(2) : '00'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="700" color="primary">
                                                        ${(applyMargin(price.machinePriceUsd) + price.setupFee + price.transportFee + (price.deposit || 0)).toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default function Prices() {
    const [machines, setMachines] = useState<SegmentsMachineResponseDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Filter and search states
    const [searchQuery, setSearchQuery] = useState('');
    const [vendorFilter, setVendorFilter] = useState<string>('all');
    const [algorithmFilter, setAlgorithmFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [marginPercentage, setMarginPercentage] = useState<number>(0);

    useEffect(() => {
        fetchPrices();
    }, []);

    const fetchPrices = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get<SegmentsMachineResponseDto[]>(
                `${API_PATH}/price-predictions/v6/machines/nomargin`,
                getAxiosConfig()
            );
            setMachines(response.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch price data');
            console.error('Error fetching prices:', err);
        } finally {
            setLoading(false);
        }
    };

    const regeneratePrices = async () => {
        setLoading(true);
        setError(null);
        try {
            await axios.get(
                `${API_PATH}/price-predictions/force-generate`,
                getAxiosConfig()
            );
            // Refresh data after regeneration
            await fetchPrices();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to regenerate prices');
            console.error('Error regenerating prices:', err);
        } finally {
            setLoading(false);
        }
    };

    // Get unique values for filters
    const uniqueVendors = useMemo(() => {
        return Array.from(new Set(machines.map(m => m.vendor))).sort();
    }, [machines]);

    const uniqueAlgorithms = useMemo(() => {
        return Array.from(new Set(machines.map(m => m.algorithm))).sort();
    }, [machines]);

    const uniqueStatuses = useMemo(() => {
        return Array.from(new Set(machines.map(m => m.inventoryStatus))).sort();
    }, [machines]);

    // Filtered machines
    const filteredMachines = useMemo(() => {
        return machines.filter(machine => {
            const matchesSearch = searchQuery === '' ||
                (machine.machineModelName?.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (machine.vendor?.toLowerCase().includes(searchQuery.toLowerCase())) ||
                (machine.algorithm?.toLowerCase().includes(searchQuery.toLowerCase()));

            const matchesVendor = vendorFilter === 'all' || machine.vendor === vendorFilter;
            const matchesAlgorithm = algorithmFilter === 'all' || machine.algorithm === algorithmFilter;
            const matchesStatus = statusFilter === 'all' || machine.inventoryStatus === statusFilter;

            return matchesSearch && matchesVendor && matchesAlgorithm && matchesStatus;
        });
    }, [machines, searchQuery, vendorFilter, algorithmFilter, statusFilter]);

    const clearFilters = () => {
        setSearchQuery('');
        setVendorFilter('all');
        setAlgorithmFilter('all');
        setStatusFilter('all');
        setMarginPercentage(0);
    };

    const hasActiveFilters = searchQuery !== '' || vendorFilter !== 'all' ||
        algorithmFilter !== 'all' || statusFilter !== 'all';

    const downloadCSV = () => {
        if (filteredMachines.length === 0) return;

        // Helper function to apply margin
        const applyMargin = (price: number) => {
            return price * (1 + marginPercentage / 100);
        };

        // Create CSV header
        const headers = [
            'Machine Model',
            'Vendor',
            'Algorithm',
            'Hashrate',
            'Hashrate Unit',
            'Power (W)',
            'Inventory Status',
            'Batch Reference ID',
            'Machine Price (USD)',
            'Hosting Cost',
            'Setup Fee',
            'Transport Fee',
            'Margin %'
        ];

        // Create CSV rows
        const rows: string[][] = [];
        filteredMachines.forEach((machine) => {
            machine.prices.forEach((price) => {
                rows.push([
                    machine.machineModelName,
                    machine.vendor,
                    machine.algorithm,
                    machine.machineHashrate.toString(),
                    machine.hashrateUnit,
                    machine.machinePower.toString(),
                    machine.inventoryStatus,
                    machine.batchReferenceId,
                    applyMargin(price.machinePriceUsd).toFixed(2),
                    price.hostingCost.toFixed(2),
                    price.setupFee.toFixed(2),
                    price.transportFee.toFixed(2),
                    marginPercentage.toString()
                ]);
            });
        });

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `machine-prices-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={60} thickness={4} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
                <Button variant="contained" onClick={fetchPrices} sx={{ mt: 2 }}>
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Box p={isMobile ? 2 : 3}>
            {/* Header with gradient */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                    p: 3,
                    mb: 3,
                    color: 'white',
                    boxShadow: 3
                }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight="700" gutterBottom>
                            Machine Prices
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {filteredMachines.length} of {machines.length} machines
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Tooltip title="Refresh data">
                            <IconButton
                                onClick={fetchPrices}
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={regeneratePrices}
                            disabled={loading}
                            sx={{
                                bgcolor: 'white',
                                color: 'primary.main',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                            }}
                        >
                            Regenerate Prices
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={downloadCSV}
                            disabled={filteredMachines.length === 0}
                            sx={{
                                bgcolor: 'white',
                                color: 'primary.main',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                            }}
                        >
                            Download CSV
                        </Button>
                    </Stack>
                </Box>
            </Box>

            {/* Filters Section */}
            <Fade in={true}>
                <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                        <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" fontWeight="600">
                            Filters & Search
                        </Typography>
                        {hasActiveFilters && (
                            <Chip
                                label="Active"
                                color="primary"
                                size="small"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        {/* Search */}
                        <Grid item xs={12} md={6} lg={3}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search machines..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchQuery('')}>
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        {/* Vendor Filter */}
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Vendor</InputLabel>
                                <Select
                                    value={vendorFilter}
                                    label="Vendor"
                                    onChange={(e) => setVendorFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Vendors</MenuItem>
                                    {uniqueVendors.map(vendor => (
                                        <MenuItem key={vendor} value={vendor}>{vendor}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Algorithm Filter */}
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Algorithm</InputLabel>
                                <Select
                                    value={algorithmFilter}
                                    label="Algorithm"
                                    onChange={(e) => setAlgorithmFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Algorithms</MenuItem>
                                    {uniqueAlgorithms.map(algo => (
                                        <MenuItem key={algo} value={algo}>{algo}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Status Filter */}
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Statuses</MenuItem>
                                    {uniqueStatuses.map(status => (
                                        <MenuItem key={status} value={status}>
                                            {status.replace(/_/g, ' ')}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Margin Percentage Input */}
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Margin %"
                                value={marginPercentage}
                                onChange={(e) => setMarginPercentage(Number(e.target.value))}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                inputProps={{
                                    min: 0,
                                    max: 100,
                                    step: 0.1
                                }}
                            />
                        </Grid>

                        {/* Clear Filters Button */}
                        <Grid item xs={12} sm={6} md={3} lg={1}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={clearFilters}
                                disabled={!hasActiveFilters}
                            >
                                Clear Filters
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </Fade>

            {/* Results */}
            {filteredMachines.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {machines.length === 0
                        ? 'No price data available'
                        : 'No machines match your filters. Try adjusting your search criteria.'}
                </Alert>
            ) : isMobile ? (
                <Box>
                    {filteredMachines.map((machine, index) => (
                        <MachineRow
                            key={machine.batchReferenceId || `${machine.machineModelName}-${index}`}
                            machine={machine}
                            isMobile={isMobile}
                            marginPercentage={marginPercentage}
                        />
                    ))}
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                    <Table aria-label="machine prices table">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'primary.main' }}>
                                <TableCell sx={{ color: 'white' }} />
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Machine Model</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Batch Reference</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Vendor</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Algorithm</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Hashrate</TableCell>
                                <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Power</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Setup Type</TableCell>
                                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Price Options</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredMachines.map((machine, index) => (
                                <MachineRow
                                    key={machine.batchReferenceId || `${machine.machineModelName}-${index}`}
                                    machine={machine}
                                    isMobile={isMobile}
                                    marginPercentage={marginPercentage}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
